import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  X,
  Menu,
  LayoutDashboard,
  ClipboardCheck,
  CalendarDays,
  Milk,
  Apple,
  FileText,
  Bell,
  Users,
  Shield,
  Truck,
  Boxes,
  PackageCheck,
  PackageMinus,
  Wine,
  RefreshCcw,
  ClipboardList,
  Tags,
  Thermometer,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import brandDesktopLogoUrl from '../../assets/branding/png/onemart-logo.png';

const Sidebar = ({
  isWebMode = false,
  isMobileOpen = false,
  isDesktopCollapsed = false,
  onToggleSidebar,
  onCloseMobile,
}) => {
  const { user, hasFeatureAccess, isAdmin } = useAuthStore();
  const canAccessGoodsReceived = hasFeatureAccess('goods_received.task');
  const canAccessDairyOrderRecording = hasFeatureAccess('dairy_order.task');
  const canAccessProductReturnsTask = hasFeatureAccess('product_returns.task');
  const canAccessProduceInventoryTracker = hasFeatureAccess('produce_tracker.task');
  const canAccessFullInventoryCountTask = hasFeatureAccess('inventory_count.task');
  const canAccessDailyLiquorCount = hasFeatureAccess('daily_liquor_count.task');
  const canAccessTaskBoard = hasFeatureAccess('tasks.board');
  const canAccessLogisticsDesk = hasFeatureAccess('logistics.manage');
  const canAccessWorkforceRoster = hasFeatureAccess('workforce_roster.view');
  const canAccessReportsHub = hasFeatureAccess('reports.hub');
  const canAccessUserManagement = hasFeatureAccess('users.manage');
  const canAccessFeatureAccessManagement = hasFeatureAccess('admin.feature_access');
  const canAccessCategoryClassifications = hasFeatureAccess('admin.category_classifications');
  const canAccessAssetManagement = isAdmin() || hasFeatureAccess('admin.assets');
  const canAccessProductSyncControl = hasFeatureAccess('products.sync');
  const canAccessItemChangeLog = hasFeatureAccess('item_change_log.page');

  // Common nav items for all roles
  const commonNavItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', iconTone: 'bg-indigo-100 text-indigo-700' },
    ...(canAccessTaskBoard ? [{ name: 'Tasks', icon: ClipboardCheck, path: '/tasks', iconTone: 'bg-cyan-100 text-cyan-700' }] : []),
    ...(canAccessLogisticsDesk ? [{ name: 'Logistic Desk', icon: Truck, path: '/logistics-desk', iconTone: 'bg-sky-100 text-sky-700' }] : []),
    ...(canAccessDairyOrderRecording ? [{ name: 'Dairy Order', icon: Milk, path: '/dairy-order-recording', iconTone: 'bg-blue-100 text-blue-700' }] : []),
    ...(canAccessProductReturnsTask ? [{ name: 'Product Returns', icon: PackageMinus, path: '/product-returns', iconTone: 'bg-rose-100 text-rose-700' }] : []),
    ...(canAccessItemChangeLog ? [{ name: 'Item Change Log', icon: ClipboardList, path: '/item-change-log', iconTone: 'bg-stone-100 text-stone-700' }] : []),
    ...(canAccessProduceInventoryTracker ? [{ name: 'Produce Tracker', icon: Apple, path: '/produce-inventory-tracker', iconTone: 'bg-lime-100 text-lime-700' }] : []),
    ...(canAccessFullInventoryCountTask ? [{ name: 'Inventory Count', icon: Boxes, path: '/full-inventory-count', iconTone: 'bg-amber-100 text-amber-700' }] : []),
    ...(canAccessDailyLiquorCount ? [{ name: 'Daily Liquor Count', icon: Wine, path: '/daily-liquor-count', iconTone: 'bg-violet-100 text-violet-700' }] : []),
    ...(canAccessGoodsReceived ? [{ name: 'Goods Received', icon: PackageCheck, path: '/goods-received', iconTone: 'bg-emerald-100 text-emerald-700' }] : []),
    ...(canAccessWorkforceRoster ? [{ name: 'Roster', icon: CalendarDays, path: '/workforce-roster', iconTone: 'bg-sky-100 text-sky-700' }] : []),
    ...(canAccessReportsHub ? [{ name: 'Reports', icon: FileText, path: '/reports', iconTone: 'bg-fuchsia-100 text-fuchsia-700' }] : []),
    { name: 'Notice Board', icon: Bell, path: '/notifications', iconTone: 'bg-orange-100 text-orange-700' },
  ];
  const navItems = [...commonNavItems];

  const adminItems = [
    ...(canAccessUserManagement ? [{ name: 'User Management', icon: Users, path: '/admin/users', iconTone: 'bg-slate-100 text-slate-700' }] : []),
    ...(canAccessFeatureAccessManagement ? [{ name: 'Feature Access', icon: Shield, path: '/admin/feature-access', iconTone: 'bg-slate-100 text-slate-700' }] : []),
    ...(canAccessCategoryClassifications ? [{ name: 'Category Groups', icon: Tags, path: '/admin/category-classifications', iconTone: 'bg-slate-100 text-slate-700' }] : []),
    ...(canAccessAssetManagement ? [{ name: 'Store Assets', icon: Thermometer, path: '/admin/assets', iconTone: 'bg-slate-100 text-slate-700' }] : []),
    ...(canAccessProductSyncControl ? [{ name: 'Product Snapshot', icon: RefreshCcw, path: '/admin/product-sync', iconTone: 'bg-slate-100 text-slate-700' }] : []),
  ];
  const isOverlayOpen = !isWebMode && isMobileOpen;

  const handleCloseMobile = () => {
    if (typeof onCloseMobile === 'function') {
      onCloseMobile();
    }
  };

  const handleToggleSidebar = () => {
    if (typeof onToggleSidebar === 'function') {
      onToggleSidebar();
    }
  };

  const linkClasses = (isActive, collapsed = false) => (
    `flex items-center rounded-lg py-2 transition-colors ${
      collapsed ? 'justify-center px-2' : 'space-x-3 px-4'
    } ${
      isActive
        ? 'bg-primary-50 text-primary-700'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`
  );

  const renderNavIcon = (item, isActive) => (
    <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
      item.iconTone || 'bg-slate-100 text-slate-700'
    } ${isActive ? 'ring-2 ring-white/80 shadow-sm' : ''}`}>
      <item.icon className="h-4 w-4" />
    </span>
  );

  const renderBrandSignature = (className = '') => (
    <div className={`overflow-hidden ${className}`}>
      <img
        src={brandDesktopLogoUrl}
        alt="Foodmandu signature"
        className="h-10 w-full object-contain object-center"
      />
    </div>
  );

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-900/35 transition-opacity duration-200 ${
          isOverlayOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={handleCloseMobile}
        aria-hidden={!isOverlayOpen}
      />

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[88vw] flex-col bg-white shadow-xl transition-transform duration-200 ${
          isOverlayOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!isOverlayOpen}
      >
        <div className="flex items-center gap-3 border-b border-yellow-700/60 px-4 py-2 sm:px-6 yellow-400/90">
          <div className="min-w-0 flex-1">
            {renderBrandSignature('h-12 w-full max-w-[190px]')}
          </div>
          <button
            type="button"
            onClick={handleCloseMobile}
            className="shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="app-scrollbar flex-1 space-y-0.5 overflow-y-auto px-3 py-3 sm:px-4 sm:py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              onClick={handleCloseMobile}
              className={({ isActive }) => linkClasses(isActive)}
            >
              {({ isActive }) => (
                <>
                  {renderNavIcon(item, isActive)}
                  <span className="text-sm font-medium">{item.name}</span>
                </>
              )}
            </NavLink>
          ))}

          {adminItems.length > 0 && (
            <>
              <div className="mt-4 border-t border-gray-200 pt-4">
                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Administration
                </p>
              </div>
              {adminItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.path}
                  onClick={handleCloseMobile}
                  className={({ isActive }) => linkClasses(isActive)}
                >
                  {({ isActive }) => (
                    <>
                      {renderNavIcon(item, isActive)}
                      <span className="text-sm font-medium">{item.name}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="border-t border-gray-200 p-4">
          <NavLink
            to="/profile"
            onClick={handleCloseMobile}
            className="flex items-center space-x-3 rounded-lg p-2 transition-colors hover:bg-gray-50"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
              <span className="font-semibold text-primary-700">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">
                {user?.name}
              </p>
              <p className="truncate text-xs text-gray-500">{user?.role}</p>
            </div>
          </NavLink>
        </div>
      </aside>

      <aside
        className={`${isWebMode ? 'fixed' : 'hidden'} inset-y-0 left-0 z-40 flex h-screen flex-col border-r border-gray-200 bg-white shadow-sm transition-[width] duration-200 ${
          isDesktopCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className={`border-b border-gray-200 ${isDesktopCollapsed ? 'px-2' : 'px-4'} h-16 flex items-center overflow-hidden bg-yellow-300`}>
          {isDesktopCollapsed ? (
            <div className="flex h-full w-full items-center justify-center">
              <button
                type="button"
                onClick={handleToggleSidebar}
                title="Expand Sidebar"
                className="rounded-lg p-2 border-2 border-white-20 text-white transition-colors hover:bg-yellow-50 hover:text-gray-900"
                aria-label="Expand sidebar"
              >
                <Menu className="h-5 w-5 shrink-10" />
              </button>
            </div>
          ) : (
            <div className="flex h-full items-center w-full gap-2">
              <div className="flex-1 flex-shrink-0 overflow-hidden">
                {renderBrandSignature('h-10 w-full max-w-[190px] flex-shrink-0')}
              </div>
              <button
                type="button"
                onClick={handleToggleSidebar}
                title="Collapse Sidebar"
                className="shrink-0 rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 ml-auto"
                aria-label="Collapse sidebar"
              >
                <Menu className="h-5 w-5 shrink-0" />
              </button>
            </div>
          )}
        </div>

        <nav className={`app-scrollbar flex-1 space-y-0.5 overflow-y-auto ${isDesktopCollapsed ? 'px-2 py-2.5' : 'px-4 py-2.5'}`}>
          {navItems.map((item) => (
            <NavLink
              key={`desktop-${item.name}`}
              to={item.path}
              title={isDesktopCollapsed ? item.name : undefined}
              className={({ isActive }) => linkClasses(isActive, isDesktopCollapsed)}
            >
              {({ isActive }) => (
                <>
                  {renderNavIcon(item, isActive)}
                  {!isDesktopCollapsed ? <span className="text-sm font-medium">{item.name}</span> : null}
                </>
              )}
            </NavLink>
          ))}

          {adminItems.length > 0 && (
            <>
              <div className={`mt-4 border-t border-gray-200 pt-4 ${isDesktopCollapsed ? 'px-2' : ''}`}>
                {!isDesktopCollapsed ? (
                  <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Administration
                  </p>
                ) : (
                  <p className="text-center text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    ADM
                  </p>
                )}
              </div>
              {adminItems.map((item) => (
                <NavLink
                  key={`desktop-admin-${item.name}`}
                  to={item.path}
                  title={isDesktopCollapsed ? item.name : undefined}
                  className={({ isActive }) => linkClasses(isActive, isDesktopCollapsed)}
                >
                  {({ isActive }) => (
                    <>
                      {renderNavIcon(item, isActive)}
                      {!isDesktopCollapsed ? <span className="text-sm font-medium">{item.name}</span> : null}
                    </>
                  )}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className={`border-t border-gray-200 ${isDesktopCollapsed ? 'p-3' : 'p-4'}`}>
          <NavLink
            to="/profile"
            title={isDesktopCollapsed ? 'Profile' : undefined}
            className={`flex items-center rounded-lg p-2 transition-colors hover:bg-gray-50 ${
              isDesktopCollapsed ? 'justify-center' : 'space-x-3'
            }`}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
              <span className="font-semibold text-primary-700">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            {!isDesktopCollapsed ? (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">
                  {user?.name}
                </p>
                <p className="truncate text-xs text-gray-500">{user?.role}</p>
              </div>
            ) : null}
          </NavLink>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
